import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import DocumentTitle from 'react-document-title';
import { useQueryClient } from 'react-query';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { usePrevious } from '@hivebrite/react-hooks';

import { CardJobLoader } from 'hb-react/frontoffice/apps/JobsV2/components/CardJob';
import { CardJobSmallLoader } from 'hb-react/frontoffice/apps/JobsV2/components/CardJobSmall';
import JobDetails from 'hb-react/frontoffice/apps/JobsV2/components/JobDetails';
import {
  fetchTagOptions,
  makeFetchItems,
  makeFetchPinnedItems,
} from 'hb-react/frontoffice/apps/JobsV2/requests';
import {
  getFilters,
  getJobsHeaderProps,
  makeItemRenderer,
} from 'hb-react/frontoffice/apps/JobsV2/utils';
import BlocksRenderer from 'hb-react/frontoffice/components/BlocksRenderer';
import {
  invalidateItems,
  ITEMS_QUERY_KEYS,
} from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import { BLOCKS } from 'hb-react/frontoffice/components/BlocksRenderer/shared';
import embeddedDetailsTemplate from 'hb-react/frontoffice/components/BlocksRenderer/templates/EmbededDetailsTemplate';
import oneColumnTemplate from 'hb-react/frontoffice/components/BlocksRenderer/templates/OneColumnTemplate';
import twoColumnsTemplate from 'hb-react/frontoffice/components/BlocksRenderer/templates/TwoColumnsTemplate';
import {
  NO_ZONE,
  withCustomZones,
} from 'hb-react/frontoffice/components/BlocksRenderer/templates/utils';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useAppHeaderVariant } from 'hb-react/shared/components/DesignSystem/AppHeader';
import JoinCard from 'hb-react/shared/components/JoinCard';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

const TEMPLATES = {
  [oneColumnTemplate.id]: withCustomZones(oneColumnTemplate, {
    [NO_ZONE]: [BLOCKS.details.id],
  }),
  [twoColumnsTemplate.id]: withCustomZones(twoColumnsTemplate, {
    [NO_ZONE]: [BLOCKS.details.id],
  }),
  [embeddedDetailsTemplate.id]: embeddedDetailsTemplate,
};

const JobsIndexPage = ({ configPage, topic, urls }) => {
  const location = useLocation();
  const { topicId } = useTopicContext();
  const navigate = useNavigate();

  const firstRender = useRef(true);

  const queryClient = useQueryClient();
  const { currentNetwork, currentUser } = useDefaultAppContexts();
  const breakpoint = useBreakpoint();
  const { flagAnalyticsChangeTitleTags } = useFeatureFlag();

  const shouldDisplaySlider = useCallback(
    (data) => isEmpty(data?.[BLOCKS.filters.id] ?? {}),
    [],
  );

  const getSelectedId = useCallback(
    (data, params) => {
      const opportunityId = params?.opportunityId;

      let jobsFound = [];

      const sliderVisible = shouldDisplaySlider(data);

      if (sliderVisible) {
        // Wait for both data to be loaded before selecting the job
        if (data?.[BLOCKS.slider.id] && data?.[ITEMS_QUERY_KEYS.jobs.items]) {
          jobsFound = [
            ...data[BLOCKS.slider.id],
            ...data[ITEMS_QUERY_KEYS.jobs.items],
          ];
        }
      } else {
        jobsFound = data?.[ITEMS_QUERY_KEYS.jobs.items];
      }

      return parseInt(
        opportunityId ||
          (['l', 'xl'].includes(breakpoint) && jobsFound?.[0]?.id),
        10,
      );
    },
    [breakpoint, shouldDisplaySlider],
  );

  const listItemsRef = useRef();

  const tagsQueryKey = 'query[kind_id]';

  const fetchItems = useMemo(
    () => makeFetchItems({ urls, tagsQueryKey }),
    [tagsQueryKey, urls],
  );

  const fetchPinnedItems = useMemo(
    () => makeFetchPinnedItems({ urls, tagsQueryKey }),
    [tagsQueryKey, urls],
  );

  const templateId =
    configPage?.config?.blocks_renderer_template || embeddedDetailsTemplate.id;

  const prevSearch = usePrevious(location.search);
  const { opportunityId } = useParams();

  // Clear the selected opportunity (by going back to the root url,
  // e.g. from /opportunities/123 to /opportunities ) when the user updates
  // the search filters/tags.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;

      return;
    }

    if (opportunityId && location.search !== prevSearch) {
      navigate(`${urls.pathRoot}${location.search}`, {
        replace: true,
      });
    }
  }, [location.search, navigate, opportunityId, prevSearch, urls.pathRoot]);
  const itemRenderer = useMemo(
    () =>
      makeItemRenderer({
        currentNetwork,
        currentUser,
        onDeleteItem: (companyId) => {
          invalidateItems(queryClient, [ITEMS_QUERY_KEYS.jobs.items, topicId]);
          if (companyId) {
            invalidateItems(
              queryClient,
              ITEMS_QUERY_KEYS.companies.jobs.items(companyId),
            );
          }
        },
        urls,
        variant: templateId === embeddedDetailsTemplate.id ? 'small' : 'normal',
        getSelectedId,
      }),
    [
      currentNetwork,
      currentUser,
      urls,
      templateId,
      getSelectedId,
      queryClient,
      topicId,
    ],
  );

  const pinnedItemRenderer = useMemo(
    () =>
      makeItemRenderer({
        currentNetwork,
        currentUser,
        urls,
        variant: 'small',
        getSelectedId,
      }),
    [currentNetwork, currentUser, urls, getSelectedId],
  );

  const filters = useMemo(() => getFilters(currentNetwork), [currentNetwork]);

  const template = TEMPLATES[templateId];

  const appHeaderProps = useMemo(
    () =>
      getJobsHeaderProps({
        appPageConfig: configPage,
        currentUser: topicId ? null : currentUser,
        urls,
        isPreview: false,
      }),
    [configPage, currentUser, topicId, urls],
  );
  const { variant } = useAppHeaderVariant(appHeaderProps);

  // restricted on private group, user is not a member of group, on FO
  const isRestrictedInPrivateGroupOnFO = !!(
    topicId &&
    !topic?.followed &&
    topic?.restricted_access &&
    currentUser
  );

  const noJobsAvailable = (data) =>
    data?.[ITEMS_QUERY_KEYS.jobs.items]?.length === 0 &&
    data?.[BLOCKS.slider.id]?.length === 0 &&
    !data?.[BLOCKS.filters.id] &&
    !data?.[BLOCKS.tags.id];

  const shouldDisplayRestrictedBlock = useCallback(
    (data) => noJobsAvailable(data) && isRestrictedInPrivateGroupOnFO,
    [isRestrictedInPrivateGroupOnFO],
  );

  return (
    <>
      {/* Only in index page with no :opportunityId in the url */}
      {flagAnalyticsChangeTitleTags && !opportunityId && (
        <DocumentTitle
          title={generatePageTitle({
            title: configPage?.menu_name || __('Opportunities'),
            networkName: getPageTitleFromNetwork(currentNetwork),
            topicName: topic?.name,
          })}
        />
      )}
      <BlocksRenderer
        templateId={template.id}
        templateComponent={template.component}
        templateZones={template.zones}
        templateProps={{
          variant,
          hasFilters: filters?.length > 0,
          offsetRightZone: true, // TwoColumnsTemplate-specific
          getSelectedId,
          goBackUrl: urls.pathRoot,
          topicId,
        }}
        blocks={[
          {
            ...BLOCKS.appHeader,
            attributes: {
              ...appHeaderProps,
            },
          },
          {
            ...BLOCKS.filters,
            attributes: {
              filters,
            },
            templateAttributes: {
              [oneColumnTemplate.id]: {
                filtersByRow: breakpoint === 'm' ? 2 : 3,
                filterRows: breakpoint === 'm' ? 2 : 1,
              },
              [twoColumnsTemplate.id]: {
                filtersByRow: breakpoint === 'm' ? 3 : 1,
                filterRows: breakpoint === 'm' ? 1 : 3,
                actionButtonsCanOverflow: true,
              },
              [embeddedDetailsTemplate.id]: {
                filtersByRow: breakpoint === 'm' ? 2 : 3,
                filterRows: breakpoint === 'm' ? 2 : 1,
              },
            },
          },
          {
            ...BLOCKS.slider,
            attributes: {
              queryId: ITEMS_QUERY_KEYS.jobs.pinnedItems,
              fetchItems: fetchPinnedItems,
              itemLoader: CardJobSmallLoader,
              itemRenderer: pinnedItemRenderer,
              title: ({ itemsSize, isLoading }) => (
                <>
                  {s__('Jobs|Highlighted Opportunities')}{' '}
                  {!isLoading && `(${itemsSize})`}
                </>
              ),
              shouldDisplay: shouldDisplaySlider,
            },
            templateAttributes: {
              [oneColumnTemplate.id]: {
                perPageBreakpoints: { s: 1, m: 2, l: 3, xl: 3 },
              },
              [twoColumnsTemplate.id]: {
                perPageBreakpoints: { s: 1, m: 2, l: 2, xl: 2 },
              },
              [embeddedDetailsTemplate.id]: {
                perPageBreakpoints: { s: 1, m: 2, l: 3, xl: 3 },
              },
            },
          },
          {
            ...BLOCKS.tags,
            attributes: {
              queryId: ITEMS_QUERY_KEYS.jobs.tags,
              canToggle: false,
              fetchTags: () => fetchTagOptions(urls.fetchTagOptions),
              showAllTag: true,
              hideSingleTag: true,
              multi: true,
              limit: 4,
            },
            templateAttributes: {
              [twoColumnsTemplate.id]: {
                label: s__('Jobs|Filter by category'),
              },
            },
          },
          {
            id: ITEMS_QUERY_KEYS.jobs.items,
            ref: listItemsRef,
            component: BLOCKS.items.component,
            attributes: {
              fetchItems,
              itemLoader:
                templateId === embeddedDetailsTemplate.id
                  ? CardJobSmallLoader
                  : CardJobLoader,
              itemRenderer,
              shouldHideIfEmpty: true,
            },
            templateAttributes: {
              [oneColumnTemplate.id]: {
                itemsByRow: breakpoint === 'm' ? 2 : 3,
              },
              [twoColumnsTemplate.id]: {
                itemsByRow: 2,
              },
              [embeddedDetailsTemplate.id]: {
                itemsByRow: 1,
                rowGap: 4,
                title: ({ itemsSize, isLoading }) => (
                  <>
                    {__('Opportunities')} {!isLoading && `(${itemsSize})`}
                  </>
                ),
              },
            },
          },
          {
            ...BLOCKS.conditionalBlock,
            attributes: {
              shouldDisplay: ({ data }) => shouldDisplayRestrictedBlock(data),
              component: JoinCard,
            },
          },
          {
            ...BLOCKS.details,
            attributes: {
              queryId: 'job',
              getSelectedId,
              getFetchUrl: (id) => urls.fetchItem({ opportunityId: id }),
              handleQueryError: (error) => {
                if (error.response.status === 403) {
                  window.location = `/login?redirect_to=${encodeURIComponent(
                    window.location.pathname,
                  )}`;
                }

                throw error;
              },
              renderData: (data, dataTestId, key) => (
                <JobDetails
                  dataTestId={dataTestId}
                  key={key}
                  job={data}
                  noBorderRadiusOnSmallScreen
                  urls={urls}
                />
              ),
            },
          },
          {
            ...BLOCKS.emptyContent,
            attributes: {
              // We want to have either the restricted block OR the emptyContent block
              shouldDisplay: ({ data }) =>
                !shouldDisplayRestrictedBlock(data) && noJobsAvailable(data),
            },
          },
        ]}
      />
    </>
  );
};

export default JobsIndexPage;
